import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "../../styles/requirement.css";

import { useLoading } from "../../context/LoadingContext";
import DataTable from "./components/common/ReusableTable";
import SearchFilter from "./components/common/SearchFilter";
import Sidebar from "./components/layout/Sidebar";
import SidebarBuyer from "./components/layout/BuyerSidebar";
import Header from "./components/layout/Header";
import config from "../../aws-exports";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  TextField,
  IconButton,
  Modal,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import {
  fetchData,
  searchfetchQueries,
  postQuery,
  uploadVideoToS3,
} from "./services/RequirementServices";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import AlertModal from "./components/common/AlerModel";

const dataIncoterms = [
  "EXW - Ex-Works",
  "FCA – Free Carrier",
  "FOB – Free on Board",
  "CIF – Cost, Insurance And Freight",
  "DAP – Delivered At Place",
  "DAT - Delivered at Terminal",
  "CPT – Carriage Paid To ",
  "DDP – Delivered Duty Paid",
  "CFR – Cost and Freight",
  "CIP – Carriage And Insurance Paid To",
  "FAS – Free Alongside Ship",
];

const packageSizeOptions = {
  sack: [
    { value: "25kg-sack", label: "25kg sack" },
    { value: "50kg-sack", label: "50kg sack" },
  ],
  bigBag: [{ value: "1000kg-big-bag", label: "1000kg big bag" }],
};

const TabPanel = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const BasicInfoTab = ({
  category,
  setCategory,
  product,
  setProduct,
  productType,
  setProductType,
  productMaster,
  productCategoryMaster,
  productTypeMaster,
  loadProducts,
  loadProductTypes,
  errors,
  isViewMode,
}) => {
  const handleCategoryChange = useCallback(
    (e) => {
      if (!isViewMode) {
        loadProducts(e.target.value);
        setCategory(e.target.value);
      }
    },
    [setCategory, loadProducts, isViewMode]
  );

  const handleProductChange = useCallback(
    (e) => {
      if (!isViewMode) {
        loadProductTypes(e.target.value);
        setProduct(e.target.value);
      }
    },
    [setProduct, loadProductTypes, isViewMode]
  );

  const handleProducTypeChange = useCallback(
    (e) => {
      if (!isViewMode) {
        setProductType(e.target.value);
      }
    },
    [setProductType, isViewMode]
  );

  return (
    <div className="row g-3">
      <div className="col-md-4">
        <FormControl sx={{ minWidth: "100%" }} error={!!errors.category}>
          <Typography variant="body2">Select Category</Typography>
          <Select
            value={category}
            onChange={handleCategoryChange}
            displayEmpty
            sx={{ backgroundColor: "white" }}
            inputProps={{ "aria-label": "Select Category" }}
            disabled={isViewMode}
          >
            <MenuItem value="" disabled>
              Select Category
            </MenuItem>
            {productCategoryMaster.map((p) => (
              <MenuItem key={p.ProductCategoryID} value={p.ProductCategoryID}>
                {p.CategoryName}
              </MenuItem>
            ))}
          </Select>
          {errors.category && (
            <Typography variant="caption" color="error">
              {errors.category}
            </Typography>
          )}
        </FormControl>
      </div>
      <div className="col-md-4">
        <FormControl sx={{ minWidth: "100%" }} error={!!errors.product}>
          <Typography variant="body2">Select Product</Typography>
          <Select
            value={product}
            onChange={handleProductChange}
            displayEmpty
            sx={{ backgroundColor: "white" }}
            inputProps={{ "aria-label": "Select Product" }}
            disabled={isViewMode}
          >
            <MenuItem value="" disabled>
              Select Product
            </MenuItem>
            {productMaster.map((p) => (
              <MenuItem key={p.ProductID} value={p.ProductID}>
                {p.ProductName}
              </MenuItem>
            ))}
          </Select>
          {errors.product && (
            <Typography variant="caption" color="error">
              {errors.product}
            </Typography>
          )}
        </FormControl>
      </div>
      <div className="col-md-4">
        <FormControl sx={{ minWidth: "100%" }}>
          <Typography variant="body2">Select Product Type</Typography>
          <Select
            value={productType}
            onChange={handleProducTypeChange}
            displayEmpty
            sx={{ backgroundColor: "white" }}
            inputProps={{ "aria-label": "Select Product Type" }}
            disabled={isViewMode}
          >
            <MenuItem value="" disabled>
              Select Product Type
            </MenuItem>
            {productTypeMaster.map((p) => (
              <MenuItem key={p.ProductTypeID} value={p.ProductTypeID}>
                {p.ProductTypeName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </div>
  );
};

const GeneralTab = ({
  validUntil,
  setValidUntil,
  incoterm,
  setIncoterm,
  locations,
  setLocations,
  deliveryPeriodFrom,
  setDeliveryPeriodFrom,
  deliveryPeriodTill,
  setDeliveryPeriodTill,
  paymentConditionFiles,
  handleOpenPaymentConditionModal,
  handleDeletePaymentConditionFile,
  dataIncoterms,
  errors,
  isViewMode,
}) => {
  const inputStyles = {
    borderRadius: "4px",
    border: "1px solid #ccc",
    padding: "4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  };

  const handleValidUntilChange = useCallback(
    (e) => {
      if (!isViewMode) setValidUntil(e.target.value);
    },
    [setValidUntil, isViewMode]
  );

  const handleIncotermChange = useCallback(
    (e) => {
      if (!isViewMode) setIncoterm(e.target.value);
    },
    [setIncoterm, isViewMode]
  );

  const handleLocationsChange = useCallback(
    (e) => {
      if (!isViewMode) setLocations(e.target.value);
    },
    [setLocations, isViewMode]
  );

  const handleDeliveryPeriodFromChange = useCallback(
    (e) => {
      if (!isViewMode) setDeliveryPeriodFrom(e.target.value);
    },
    [setDeliveryPeriodFrom, isViewMode]
  );

  const handleDeliveryPeriodTillChange = useCallback(
    (e) => {
      if (!isViewMode) setDeliveryPeriodTill(e.target.value);
    },
    [setDeliveryPeriodTill, isViewMode]
  );

  return (
    <div className="row g-3">
      <div className="col-md-6">
        <div className="mb-3">
          <Typography variant="body2">Delivery Period From</Typography>
          <TextField
            type="date"
            value={deliveryPeriodFrom}
            onChange={handleDeliveryPeriodFromChange}
            sx={{ backgroundColor: "white", width: "100%" }}
            error={!!errors.deliveryPeriodFrom}
            helperText={errors.deliveryPeriodFrom}
            inputProps={{
              "aria-label": "Delivery Period From",
              min: new Date().toISOString().split("T")[0],
            }}
            disabled={isViewMode}
          />
        </div>
        <div className="mb-3">
          <Typography variant="body2">Valid Until</Typography>
          <TextField
            type="date"
            value={validUntil}
            onChange={handleValidUntilChange}
            sx={{ backgroundColor: "white", width: "100%" }}
            error={!!errors.validUntil}
            helperText={errors.validUntil}
            inputProps={{
              "aria-label": "Valid Until",
              min: new Date().toISOString().split("T")[0],
              max: deliveryPeriodTill,
            }}
            disabled={isViewMode || !deliveryPeriodTill}
          />
        </div>

        <div className="mb-3">
          <Typography variant="body2">Delivery Location</Typography>
          <TextField
            type="text"
            value={locations}
            onChange={handleLocationsChange}
            sx={{ backgroundColor: "white", width: "100%" }}
            error={!!errors.locations}
            helperText={errors.locations}
            inputProps={{ "aria-label": "Delivery Location" }}
            disabled={isViewMode}
          />
        </div>
      </div>
      <div className="col-md-6">
        <div className="mb-3">
          <Typography variant="body2">Delivery Period Till</Typography>
          <TextField
            type="date"
            value={deliveryPeriodTill}
            onChange={handleDeliveryPeriodTillChange}
            sx={{ backgroundColor: "white", width: "100%" }}
            error={!!errors.deliveryPeriodTill}
            helperText={errors.deliveryPeriodTill}
            inputProps={{
              "aria-label": "Delivery Period Till",
              min: deliveryPeriodFrom || new Date().toISOString().split("T")[0],
            }}
            disabled={isViewMode || !deliveryPeriodFrom}
          />
        </div>
        <div className="mb-3">
          <Typography variant="body2">Incoterm</Typography>
          <Select
            value={incoterm}
            onChange={handleIncotermChange}
            displayEmpty
            sx={{ backgroundColor: "white", width: "100%" }}
            inputProps={{ "aria-label": "Select Incoterm" }}
            disabled={isViewMode}
          >
            <MenuItem value="" disabled>
              Select Incoterm
            </MenuItem>
            {dataIncoterms.map((incotermOption, index) => (
              <MenuItem key={index} value={incotermOption}>
                {incotermOption}
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className="mb-3">
          <Typography variant="body2">
            Payment Condition
            <Button
              variant="contained"
              sx={{
                marginLeft: "5px",
                marginBottom: "5px",
                backgroundColor: "#ff9800",
                "&:hover": { backgroundColor: "#fb8c00" },
                mt: 1,
              }}
              onClick={handleOpenPaymentConditionModal}
              disabled={isViewMode}
            >
              Add
            </Button>
          </Typography>
          {paymentConditionFiles.length > 0 && (
            <Box sx={{ backgroundColor: "white", p: 1, borderRadius: 1 }}>
              {paymentConditionFiles.map((file, index) => (
                <Box key={index} sx={{ ...inputStyles, mb: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "normal",
                      overflow: "visible",
                      textOverflow: "unset",
                      flexGrow: 1,
                      wordBreak: "break-word",
                    }}
                  >
                    {isViewMode ? (
                      <>
                        <a
                          href={`https://${process.env.REACT_APP_AMTPL_BUCKET}.s3.${config.aws_project_region}.amazonaws.com/public/media/amtpl/${file.name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "rgb(150 150 165)",
                            padding: "1px 1px 2px 1px",
                          }}
                        >
                          <PictureAsPdfIcon sx={{ mr: 1 }} /> {file.name}
                        </a>
                      </>
                    ) : (
                      <>{file.name}</>
                    )}
                  </Typography>
                  <IconButton
                    onClick={() => handleDeletePaymentConditionFile(index)}
                    size="small"
                    disabled={isViewMode}
                  >
                    <CloseIcon sx={{ color: "#ff0000" }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};

const ProductDetailsTab = ({
  originCountry,
  setOriginCountry,
  termsAndConditions,
  handleOpenTermsModal,
  handleDeleteTermsFile,
  otherFiles,
  handleOpenFilesModal,
  handleDeleteOtherFile,
  countrymaster,
  errors,
  isViewMode,
}) => {
  const inputStyles = {
    borderRadius: "4px",
    border: "1px solid #ccc",
    padding: "4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  };

  const handleOriginCountryChange = useCallback(
    (e) => {
      if (!isViewMode) setOriginCountry(e.target.value);
    },
    [setOriginCountry, isViewMode]
  );

  return (
    <div className="row g-3">
      <div className="col-md-6">
        <div className="mb-3">
          <Typography variant="body2">Origin Country</Typography>
          <Select
            value={originCountry}
            onChange={handleOriginCountryChange}
            displayEmpty
            sx={{ backgroundColor: "white", width: "100%" }}
            error={!!errors.originCountry}
            inputProps={{ "aria-label": "Select Origin Country" }}
            disabled={isViewMode}
          >
            <MenuItem value="" disabled>
              Select Origin Country
            </MenuItem>
            {countrymaster.map((c) => (
              <MenuItem key={c.ID} value={c.Value}>
                {c.Value}
              </MenuItem>
            ))}
          </Select>
          {errors.originCountry && (
            <Typography variant="caption" color="error">
              {errors.originCountry}
            </Typography>
          )}
        </div>
        <div className="mb-3">
          <Typography variant="body2">
            Terms & Condition
            <Button
              variant="contained"
              sx={{
                marginLeft: "5px",
                marginBottom: "5px",
                backgroundColor: "#ff9800",
                "&:hover": { backgroundColor: "#fb8c00" },
                mt: 1,
              }}
              onClick={handleOpenTermsModal}
              disabled={isViewMode}
            >
              Add
            </Button>
          </Typography>
          {termsAndConditions.length > 0 && (
            <Box sx={{ backgroundColor: "white", p: 1, borderRadius: 1 }}>
              {termsAndConditions.map((file, index) => (
                <Box key={index} sx={{ ...inputStyles, mb: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "normal",
                      overflow: "visible",
                      textOverflow: "unset",
                      flexGrow: 1,
                      wordBreak: "break-word",
                    }}
                  >
                    {isViewMode ? (
                      <>
                        <a
                          href={`https://${process.env.REACT_APP_AMTPL_BUCKET}.s3.${config.aws_project_region}.amazonaws.com/public/media/amtpl/${file.name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "rgb(150 150 165)",
                            padding: "1px 1px 2px 1px",
                          }}
                        >
                          <PictureAsPdfIcon sx={{ mr: 1 }} /> {file.name}
                        </a>
                      </>
                    ) : (
                      <>{file.name}</>
                    )}
                  </Typography>
                  <IconButton
                    onClick={() => handleDeleteTermsFile(index)}
                    size="small"
                    disabled={isViewMode}
                  >
                    <CloseIcon sx={{ color: "#ff0000" }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </div>
        <div className="mb-3">
          <Typography variant="body2">
            Other Files
            <Button
              variant="contained"
              sx={{
                marginLeft: "5px",
                marginBottom: "5px",
                backgroundColor: "#ff9800",
                "&:hover": { backgroundColor: "#fb8c00" },
                mt: 1,
              }}
              onClick={handleOpenFilesModal}
              disabled={isViewMode}
            >
              Add
            </Button>
          </Typography>
          {otherFiles.length > 0 && (
            <Box sx={{ backgroundColor: "white", p: 1, borderRadius: 1 }}>
              {otherFiles.map((file, index) => (
                <Box key={index} sx={{ ...inputStyles, mb: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "normal",
                      overflow: "visible",
                      textOverflow: "unset",
                      flexGrow: 1,
                      wordBreak: "break-word",
                    }}
                  >
                    {isViewMode ? (
                      <>
                        <a
                          href={`https://${process.env.REACT_APP_AMTPL_BUCKET}.s3.${config.aws_project_region}.amazonaws.com/public/media/amtpl/${file.name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: "rgb(150 150 165)",
                            padding: "1px 1px 2px 1px",
                          }}
                        >
                          <PictureAsPdfIcon sx={{ mr: 1 }} /> {file.name}
                        </a>
                      </>
                    ) : (
                      <>{file.name}</>
                    )}
                  </Typography>
                  <IconButton
                    onClick={() => handleDeleteOtherFile(index)}
                    size="small"
                    disabled={isViewMode}
                  >
                    <CloseIcon sx={{ color: "#ff0000" }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};

const QuantityPackagingTab = ({
  productPackaging,
  setProductPackaging,
  packageSize,
  setPackageSize,
  requestedQuantity,
  setRequestedQuantity,
  priceOffered,
  setPriceOffered,
  errors,
  isViewMode,
}) => {
  const handleProductPackagingChange = useCallback(
    (e) => {
      if (!isViewMode) {
        const value = e.target.value;
        setProductPackaging(value);
        setPackageSize("");
        if (value === "sack") setPackageSize("25kg-sack");
        else if (value === "bigBag") setPackageSize("1000kg-big-bag");
      }
    },
    [setProductPackaging, setPackageSize, isViewMode]
  );

  const handlePackageSizeChange = useCallback(
    (e) => {
      if (!isViewMode) setPackageSize(e.target.value);
    },
    [setPackageSize, isViewMode]
  );

  const handleRequestedQuantityChange = useCallback(
    (e) => {
      if (!isViewMode) setRequestedQuantity(e.target.value);
    },
    [setRequestedQuantity, isViewMode]
  );

  const handlePriceOfferedChange = useCallback(
    (e) => {
      if (!isViewMode) setPriceOffered(e.target.value);
    },
    [setPriceOffered, isViewMode]
  );

  return (
    <div className="row g-3">
      <div className="col-md-6 mb-3">
        <Typography variant="body2">Product Packaging</Typography>
        <Select
          value={productPackaging}
          onChange={handleProductPackagingChange}
          displayEmpty
          sx={{ backgroundColor: "white", width: "100%" }}
          error={!!errors.productPackaging}
          inputProps={{ "aria-label": "Select Product Packaging" }}
          disabled={isViewMode}
        >
          <MenuItem value="" disabled>
            Select Product Packaging
          </MenuItem>
          <MenuItem value="sack">Sack</MenuItem>
          <MenuItem value="bigBag">Big Bag</MenuItem>
        </Select>
        {errors.productPackaging && (
          <Typography variant="caption" color="error">
            {errors.productPackaging}
          </Typography>
        )}
      </div>
      {(productPackaging === "sack" || productPackaging === "bigBag") && (
        <div className="col-md-6 mb-3">
          <Typography variant="body2">Package Size</Typography>
          <Select
            value={packageSize}
            onChange={handlePackageSizeChange}
            displayEmpty
            sx={{ backgroundColor: "white", width: "100%" }}
            inputProps={{ "aria-label": "Select Package Size" }}
            disabled={isViewMode}
          >
            {productPackaging === "sack" &&
              packageSizeOptions.sack.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            {productPackaging === "bigBag" &&
              packageSizeOptions.bigBag.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
          </Select>
        </div>
      )}
      <div className="col-md-6 mb-3">
        <Typography variant="body2">Requested Quantity (in MT)</Typography>
        <TextField
          value={requestedQuantity}
          onChange={handleRequestedQuantityChange}
          sx={{ backgroundColor: "white", width: "100%" }}
          type="number"
          error={!!errors.requestedQuantity}
          helperText={errors.requestedQuantity}
          inputProps={{ "aria-label": "Requested Quantity" }}
          disabled={isViewMode}
        />
      </div>
      <div className="col-md-6 mb-3">
        <Typography variant="body2">Price Offered (in USD/MT)</Typography>
        <TextField
          value={priceOffered}
          onChange={handlePriceOfferedChange}
          sx={{ backgroundColor: "white", width: "100%" }}
          type="number"
          error={!!errors.priceOffered}
          helperText={errors.priceOffered}
          inputProps={{ "aria-label": "Price Offered" }}
          disabled={isViewMode}
        />
      </div>
    </div>
  );
};

const RequirementModule = () => {
  const location = useLocation();
  const initialFilter = location.state?.selectedFilter || "Requirement";
  const [activeUser, setActiveUser] = useState("");
  const [selectedFilter, setSelectedFilter] = useState(initialFilter);
  const [selectedRow, setSelectedRow] = useState(null);
  const { setLoading } = useLoading();
  const navigate = useNavigate();

  const [tabValue, setTabValue] = useState(0);
  const [category, setCategory] = useState("");
  const [product, setProduct] = useState("");
  const [productType, setProductType] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [incoterm, setIncoterm] = useState("");
  const [locations, setLocations] = useState("");
  const [deliveryPeriodFrom, setDeliveryPeriodFrom] = useState("");
  const [deliveryPeriodTill, setDeliveryPeriodTill] = useState("");
  const [paymentCondition, setPaymentCondition] = useState("");
  const [productCondition, setProductCondition] = useState("");
  const [productPackaging, setProductPackaging] = useState("");
  const [packageSize, setPackageSize] = useState("");
  const [requestedQuantity, setRequestedQuantity] = useState("");
  const [priceOffered, setPriceOffered] = useState("");
  const [originCountry, setOriginCountry] = useState("");
  const [termsAndConditions, setTermsAndConditions] = useState([]);
  const [otherFiles, setOtherFiles] = useState([]);
  const [paymentConditionFiles, setPaymentConditionFiles] = useState([]);
  const [locationImage, setLocationImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [editRequirementsID, setEditRequirementsID] = useState(null);
  const [isFormActive, setIsFormActive] = useState(false);

  const [openTermsModal, setOpenTermsModal] = useState(false);
  const [openFilesModal, setOpenFilesModal] = useState(false);
  const [openPaymentConditionModal, setOpenPaymentConditionModal] =
    useState(false);
  const [tempTermsFiles, setTempTermsFiles] = useState([]);
  const [tempOtherFiles, setTempOtherFiles] = useState([]);
  const [tempPaymentConditionFiles, setTempPaymentConditionFiles] = useState(
    []
  );

  const [productCategoryMaster, setProductCategoryMaster] = useState([]);
  const [productMaster, setProductMaster] = useState([]);
  const [productTypeMaster, setProductTypeMaster] = useState([]);
  const [allProductMaster, setAllProductMaster] = useState([]);
  const [countrymaster, setCountryMaster] = useState([]);
  const [incotermsMaster, setIncotermsMaster] = useState([]);
  const [buyerInfo, setBuyerInfo] = useState([]);
  const [requirementList, setRequirementList] = useState([]);
  const [rowStart, setRowStart] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [searchValues, setSearchValues] = useState({
    requirementNumber: "",
    deliveryDate: "",
    requirementDate: "",
    marketPlacement: "",
    status: "",
  });

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const loadProductCategories = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProductCategory",
          userOrder: "CategoryName",
          rowStart: 0,
          pageSize: 1,
        }),
      ]);
      const totalCount = initialResponse?.total || initialResponse?.length || 0;
      if (totalCount > 0) {
        const [allProductCats] = await Promise.all([
          searchfetchQueries({
            target: "listProductCategory",
            userOrder: "CategoryName",
            rowStart: 0,
            pageSize: totalCount,
          }),
        ]);
        setProductCategoryMaster(allProductCats);
      } else {
        setProductCategoryMaster([]);
      }
    } catch (error) {
      console.error("Error loading product categories: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadAllProducts = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProducts",
          userOrder: "ProductID",
          rowStart: 0,
          pageSize: 1,
        }),
      ]);
      const totalCount = initialResponse?.total || initialResponse?.length || 0;
      if (totalCount > 0) {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProducts",
            userOrder: "ProductID",
            rowStart: 0,
            pageSize: totalCount,
          }),
        ]);
        setAllProductMaster(allProducts);
      } else {
        setAllProductMaster([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadProducts = useCallback(
    async (productCategoryId) => {
      setLoading(true);
      try {
        const [initialResponse] = await Promise.all([
          fetchData({
            target: "listProducts",
            userOrder: "ProductID",
            rowStart: 0,
            pageSize: 1,
            filterTerms: productCategoryId
              ? { ProductCategoryID: productCategoryId }
              : {},
          }),
        ]);
        const totalCount =
          initialResponse?.total || initialResponse?.length || 0;
        if (totalCount > 0) {
          const [allProducts] = await Promise.all([
            searchfetchQueries({
              target: "listProducts",
              userOrder: "ProductID",
              rowStart: 0,
              pageSize: totalCount,
              filterTerms: productCategoryId
                ? { ProductCategoryID: productCategoryId }
                : {},
            }),
          ]);
          setProductMaster(allProducts);
        } else {
          setProductMaster([]);
        }
      } catch (error) {
        console.error("Error loading products: ", error);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const loadProductTypes = useCallback(
    async (productId) => {
      setLoading(true);
      try {
        const [initialResponse] = await Promise.all([
          fetchData({
            target: "listProductType",
            userOrder: "ProductTypeName",
            rowStart: 0,
            pageSize: 1,
            filterTerms: productId ? { ProductID: productId } : {},
          }),
        ]);
        const totalCount =
          initialResponse?.total || initialResponse?.length || 0;
        if (totalCount > 0) {
          const [allProductTypes] = await Promise.all([
            searchfetchQueries({
              target: "listProductType",
              userOrder: "ProductTypeName",
              rowStart: 0,
              pageSize: totalCount,
              filterTerms: productId ? { ProductID: productId } : {},
            }),
          ]);
          setProductTypeMaster(allProductTypes || []);
        } else {
          setProductTypeMaster([]);
        }
      } catch (error) {
        console.error("Error loading product types: ", error);
        setProductTypeMaster([]);
        setModal({
          show: true,
          type: "error",
          title: "Error",
          message: "Failed to load product types.",
          autoClose: false,
        });
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const loadCountries = useCallback(async () => {
    setLoading(true);
    try {
      const [allCountries] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: { Type: "Country" },
        }),
      ]);
      if (allCountries.items.length > 0) {
        setCountryMaster(allCountries.items);
      } else {
        setCountryMaster([]);
      }
    } catch (error) {
      console.error("Error loading countries: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadIncoterms = useCallback(async () => {
    setLoading(true);
    try {
      const [allIncoterms] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: { Type: "Incoterms" },
        }),
      ]);
      if (allIncoterms.items.length > 0) {
        setIncotermsMaster(allIncoterms.items);
      } else {
        setIncotermsMaster([]);
      }
    } catch (error) {
      console.error("Error loading Incoterms: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_BUYERS")) {
          await Auth.signOut();
          navigate("/login/buyer");
        } else {
          setActiveUser(user.attributes.email);
          getBuyerInfo(user.attributes.email);
        }
      } catch (error) {
        await Auth.signOut();
        navigate("/login/buyer");
      }
    };
    checkUser();
    loadProductCategories();
    loadAllProducts();
    loadCountries();
  }, [navigate, loadProductCategories, loadAllProducts, loadCountries]);

  const loadPaymentConditions = useCallback(async (requirementsID) => {
    try {
      const response = await fetchData({
        target: "listPaymentConditions",
        userOrder: "PaymentConditionsID",
        rowStart: 0,
        pageSize: 100,
        filterTerms: { RequirementsID: requirementsID },
      });
      return response.items || [];
    } catch (error) {
      console.error("Error loading payment conditions:", error);
      return [];
    }
  }, []);

  const loadRequirementProductInfo = useCallback(async (requirementsID) => {
    try {
      const response = await fetchData({
        target: "listRequirementProductInfo",
        userOrder: "RequirementProductInfoID",
        rowStart: 0,
        pageSize: 100,
        filterTerms: { RequirementsID: requirementsID },
      });
      return response.items || [];
    } catch (error) {
      console.error("Error loading requirement product info:", error);
      return [];
    }
  }, []);

  const getBuyerInfo = useCallback(async (buyerEmail) => {
    try {
      const response = await fetchData({
        target: "listAMTPLUsers",
        userOrder: "AMTPLUserID",
        rowStart: 0,
        pageSize: 100,
        filterTerms: { EmailID: buyerEmail },
      });
      setBuyerInfo(response.items || []);
    } catch (error) {
      console.error("Error loading buyer info:", error);
      setBuyerInfo([]);
    }
  }, []);

  const handleDataFilterChange = useCallback(
    (event) => {
      const selectedValue = event.target.value;
      setSelectedFilter(selectedValue);
      if (
        selectedValue === "All Offers" ||
        selectedValue === "Shortlisted Offers" ||
        selectedValue === "Queried Offers" ||
        selectedValue === "BAFO"
      ) {
        navigate("/buyer", { state: { selectedFilter: selectedValue } });
      }
    },
    [navigate]
  );

  const searchRequirements = useCallback(
    async (searchValue, newRowStart = rowStart) => {
      setLoading(true);
      try {
        const body = {
          target: "listRequirements",
          userOrder: "RequirementsID",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms: {
            BuyerEmailID: activeUser,
            ...(searchValue.requirementNumber && {
              RequirementNumber: searchValue.requirementNumber,
            }),
            ...(searchValue.deliveryDate && {
              DeliveryDate: searchValue.deliveryDate,
            }),
            ...(searchValue.requirementDate && {
              RequirementDate: searchValue.requirementDate,
            }),
            ...(searchValue.marketPlacement && {
              MarketPlacement: searchValue.marketPlacement,
            }),
            ...(searchValue.status && {
              Status: searchValue.status,
            }),
          },
        };
        const response = await fetchData(body);
        setRequirementList(response.items || response);
        setTotalItems(response.total || 0);
      } catch (error) {
        console.error("Error loading requirements: ", error);
        setRequirementList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, rowStart, setLoading, activeUser]
  );

  const handleReset = useCallback(() => {
    setCategory("");
    setProductType("");
    setProduct("");
    setValidUntil("");
    setIncoterm("");
    setLocations("");
    setDeliveryPeriodFrom("");
    setDeliveryPeriodTill("");
    setPaymentCondition("");
    setProductCondition("");
    setProductPackaging("");
    setPackageSize("");
    setRequestedQuantity("");
    setPriceOffered("");
    setOriginCountry("");
    setTermsAndConditions([]);
    setOtherFiles([]);
    setPaymentConditionFiles([]);
    setLocationImage(null);
    setTempTermsFiles([]);
    setTempOtherFiles([]);
    setTempPaymentConditionFiles([]);
    setErrors({});
    setIsEditMode(false);
    setIsViewMode(false);
    setEditRequirementsID(null);
    setSelectedRow(null);
    setIsFormActive(false);
  }, []);

  const parseProductPackaging = (packagingString) => {
    try {
      const cleanedString = packagingString.replace(/[{}]/g, "");
      const keyValuePairs = cleanedString.split(", ").reduce((acc, pair) => {
        const [key, value] = pair.split("=");
        acc[key] = value;
        return acc;
      }, {});
      return { item: keyValuePairs.item || "", size: keyValuePairs.size || "" };
    } catch (error) {
      console.error("Error parsing ProductPackaging:", error);
      return { item: "", size: "" };
    }
  };

  const handleRowClick = useCallback(
    async (row) => {
      setSelectedRow(row);
      setIsViewMode(true);
      setIsEditMode(false);
      setEditRequirementsID(row.RequirementsID);
      setIsFormActive(false);

      setLoading(true);
      try {
        const requirementProductInfo = await loadRequirementProductInfo(
          row.RequirementsID
        );
        const productInfo = requirementProductInfo[0] || {};
        const productData = allProductMaster.find(
          (p) => p.ProductID === row.ProductID
        );
        const productCategoryID = productData?.ProductCategoryID || "";

        setProductMaster([]);
        setProductTypeMaster([]);
        setProduct("");
        setProductType("");

        if (productCategoryID) {
          setCategory(productCategoryID);
          await loadProducts(productCategoryID);
        } else {
          setCategory("");
          setProductMaster([]);
        }

        if (row.ProductID) {
          setProduct(row.ProductID);
          await loadProductTypes(row.ProductID);
        } else {
          setProduct("");
          setProductTypeMaster([]);
        }

        setProductType(productInfo.ProductTypeID || "");
        const packaging = parseProductPackaging(row.ProductPackaging || "");
        setProductPackaging(packaging.item);
        setPackageSize(packaging.size);
        setValidUntil(
          row.ValidUntil ? dayjs(row.ValidUntil).format("YYYY-MM-DD") : ""
        );
        setIncoterm(row.Incoterm || "");
        setLocations(row.DeliveryLocation || "");
        setDeliveryPeriodFrom(
          row.DeliveryPeriodFrom
            ? dayjs(row.DeliveryPeriodFrom).format("YYYY-MM-DD")
            : ""
        );
        setDeliveryPeriodTill(
          row.DeliveryPeriodTill
            ? dayjs(row.DeliveryPeriodTill).format("YYYY-MM-DD")
            : ""
        );
        setOriginCountry(row.OriginCountry || "");
        setRequestedQuantity(row.RequestedQuantity || row.Quantity || "");
        setPriceOffered(row.PriceOffered || row.Price || "");
        setPaymentCondition("");
        setProductCondition("");
        setLocationImage(null);

        const paymentConditions = await loadPaymentConditions(
          row.RequirementsID
        );
        const paymentFiles = paymentConditions
          .filter((pc) => pc.PaymentOptions)
          .map(
            (pc) =>
              new File([""], pc.PaymentOptions.split("/").pop(), {
                type: "application/pdf",
              })
          );
        const termsFiles = paymentConditions
          .filter((pc) => pc.TermsConditionsFilePath)
          .map(
            (pc) =>
              new File([""], pc.TermsConditionsFilePath.split("/").pop(), {
                type: "application/pdf",
              })
          );
        const otherFilesData = paymentConditions
          .filter((pc) => pc.OthersFilePath)
          .map(
            (pc) =>
              new File([""], pc.OthersFilePath.split("/").pop(), {
                type: "application/pdf",
              })
          );

        setPaymentConditionFiles(paymentFiles);
        console.log("paymentFiles****", paymentFiles);
        setTermsAndConditions(termsFiles);
        setOtherFiles(otherFilesData);
        setTabValue(0);
      } catch (error) {
        console.error("Error loading row data:", error);
        setModal({
          show: true,
          type: "error",
          title: "Error",
          message: "Failed to load requirement data for viewing.",
          autoClose: false,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      loadPaymentConditions,
      loadRequirementProductInfo,
      loadProducts,
      loadProductTypes,
      allProductMaster,
      setLoading,
    ]
  );

  const handleEditClick = useCallback(() => {
    if (!selectedRow || !selectedRow.RequirementsID) {
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "No requirement selected for editing.",
        autoClose: false,
      });
      return;
    }
    setIsEditMode(true);
    setIsViewMode(false);
    setIsFormActive(true);
  }, [selectedRow]);

  const handleAddClick = useCallback(() => {
    handleReset();
    setIsEditMode(false);
    setIsViewMode(false);
    setIsFormActive(true);
  }, [handleReset]);

  const fields = [
    {
      name: "requirementNumber",
      label: "Requirement Number",
      type: "text",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, requirementNumber: value })),
      value: searchValues.requirementNumber,
    },
    {
      name: "deliveryDate",
      label: "Delivery Date",
      type: "date",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, deliveryDate: value })),
      value: searchValues.deliveryDate,
    },
    {
      name: "requirementDate",
      label: "Requirement Date",
      type: "date",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, requirementDate: value })),
      value: searchValues.requirementDate,
    },
    {
      name: "marketPlacement",
      label: "Market Placement",
      type: "dropdown",
      options: [
        { value: "Yes", label: "Yes" },
        { value: "No", label: "No" },
      ],
      value: searchValues.marketPlacement,
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, marketPlacement: value })),
    },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: [
        { value: "In Review", label: "In Review" },
        { value: "Approved", label: "Approved" },
        { value: "Rejected", label: "Rejected" },
      ],
      value: searchValues.status,
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, status: value })),
    },
  ];

  const columns = [
    { id: "RequirementNumber", numeric: false, label: "Requirement Number" },
    {
      id: "ProductID",
      numeric: false,
      label: "Product Name",
      renderCell: (row) => {
        const pn = allProductMaster.find(
          (item) => item.ProductID === row.ProductID
        );
        return pn ? pn.ProductName : "";
      },
    },
    { id: "Quantity", numeric: true, label: "Quantity (MT)" },
    { id: "Price", numeric: true, label: "Price (in USD/MT)" },
    {
      id: "DeliveryDate",
      numeric: false,
      label: "Delivery Date",
      renderCell: (row) =>
        row.DeliveryDate ? dayjs(row.DeliveryDate).format("DD-MM-YYYY") : "",
    },
    {
      id: "RequirementDate",
      numeric: false,
      label: "Requirement Date",
      renderCell: (row) =>
        row.RequirementDate
          ? dayjs(row.RequirementDate).format("DD-MM-YYYY")
          : "",
    },
    { id: "MarketPlacement", numeric: false, label: "Market Placement" },
    { id: "Status", numeric: false, label: "Status" },
  ];

  const handleSearch = (searchValues) => {
    searchRequirements(searchValues);
  };

  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};
    const today = dayjs().startOf("day");

    if (!product) newErrors.product = "Product is required";
    if (!validUntil) {
      newErrors.validUntil = "Valid until date is required";
    } else if (!dayjs(validUntil).isValid()) {
      newErrors.validUntil = "Invalid date format";
    } else if (dayjs(validUntil).isBefore(today)) {
      newErrors.validUntil = "Valid until must be a future date";
    }
    if (!locations) {
      newErrors.locations = "Delivery location is required";
    } else if (locations.length > 100) {
      newErrors.locations = "Delivery location must be 100 characters or less";
    }
    if (!deliveryPeriodFrom) {
      newErrors.deliveryPeriodFrom = "Delivery period from is required";
    } else if (!dayjs(deliveryPeriodFrom).isValid()) {
      newErrors.deliveryPeriodFrom = "Invalid date format";
    } else if (
      dayjs(deliveryPeriodFrom).isBefore(today) &&
      isEditMode === false
    ) {
      newErrors.deliveryPeriodFrom =
        "Delivery period from cannot be in the past";
    }
    if (!deliveryPeriodTill) {
      newErrors.deliveryPeriodTill = "Delivery period till is required";
    } else if (!dayjs(deliveryPeriodTill).isValid()) {
      newErrors.deliveryPeriodTill = "Invalid date format";
    } else if (
      deliveryPeriodFrom &&
      dayjs(deliveryPeriodTill).isBefore(dayjs(deliveryPeriodFrom))
    ) {
      newErrors.deliveryPeriodTill =
        "Delivery period till must be after delivery period from";
    }
    if (!originCountry) newErrors.originCountry = "Origin country is required";
    if (!productPackaging)
      newErrors.productPackaging = "Product packaging is required";
    if (!requestedQuantity) {
      newErrors.requestedQuantity = "Requested quantity is required";
    } else if (isNaN(requestedQuantity) || Number(requestedQuantity) <= 0) {
      newErrors.requestedQuantity =
        "Requested quantity must be a positive number";
    }
    if (!priceOffered) {
      newErrors.priceOffered = "Price offered is required";
    } else if (isNaN(priceOffered) || Number(priceOffered) <= 0) {
      newErrors.priceOffered = "Price offered must be a positive number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [
    product,
    validUntil,
    locations,
    deliveryPeriodFrom,
    deliveryPeriodTill,
    originCountry,
    productPackaging,
    requestedQuantity,
    priceOffered,
    isEditMode,
  ]);

  const handleSubmit = async () => {
    if (!validateForm()) {
      setModal({
        show: true,
        type: "error",
        title: "Validation Error",
        message: "Please correct the errors in the form before submitting.",
        autoClose: false,
      });
      return;
    }

    let insertedRequirementsID = null;
    const insertedPaymentConditionsIDs = [];
    setSubmitted(true);
    setLoading(true);

    try {
      const packageingValue = { item: productPackaging, size: packageSize };
      const curreDate = new Date();
      const reqNoDate = dayjs(curreDate).format("YYYYMMDD");
      const reqCount = totalItems + 1;
      const reqNumber = reqNoDate + reqCount;

      let gpEmail = buyerInfo.length > 0 ? buyerInfo[0].GPEmailID || "" : "";
      if (gpEmail === "") gpEmail = activeUser;
      let recordStatus = "In Review";
      if (selectedRow) {
        recordStatus = selectedRow.Status;
      }
      if (isEditMode) {
        if (recordStatus === "Rejected") recordStatus = "In Review";
        const body = {
          target: "updateRequirements",
          requirementsInput: {
            RequirementsID: editRequirementsID,
            RequirementNumber: selectedRow.RequirementNumber,
            GPEmailID: gpEmail,
            BuyerEmailID: activeUser,
            CreatedByEmailID: activeUser,
            ProductID: product,
            Quantity: requestedQuantity,
            Price: priceOffered,
            DeliveryDate: deliveryPeriodTill,
            RequirementDate: dayjs(curreDate).format("YYYY-MM-DD"),
            MarketPlacement: selectedRow.MarketPlacement || "No",
            ValidUntil: validUntil,
            Incoterm: incoterm,
            DeliveryLocation: locations,
            DeliveryPeriodFrom: deliveryPeriodFrom,
            DeliveryPeriodTill: deliveryPeriodTill,
            OriginCountry: originCountry,
            ProductPackaging: packageingValue,
            RequestedQuantity: requestedQuantity,
            PriceOffered: priceOffered,
            Status: recordStatus,
            Deleted: 0,
          },
        };

        await postQuery(body);

        const paymentConditions = await loadPaymentConditions(
          editRequirementsID
        );
        const requirementProductInfo = await loadRequirementProductInfo(
          editRequirementsID
        );
        await Promise.all([
          ...paymentConditions.map(async (pc) => {
            const bodyDelPC = {
              target: "deletePaymentConditions",
              PaymentConditionsID: pc.PaymentConditionsID,
            };
            await postQuery(bodyDelPC);
          }),
          ...requirementProductInfo.map(async (pi) => {
            const bodyDelPI = {
              target: "deleteRequirementProductInfo",
              RequirementProductInfoID: pi.RequirementProductInfoID,
            };
            await postQuery(bodyDelPI);
          }),
        ]);

        const bodyProductInfo = {
          target: "createRequirementProductInfo",
          requirementProductInfoInput: {
            RequirementProductInfoID: uuidv4(),
            RequirementsID: editRequirementsID,
            Category: category,
            ProductID: product,
            ProductTypeID: productType,
            Deleted: 0,
          },
        };
        await postQuery(bodyProductInfo);

        for (const file of paymentConditionFiles) {
          let uploadedFileName = null;

          if (file instanceof File && !file?.PaymentOptions) {
            console.log("***File object (type):", typeof file, file);
            // New file, needs uploading
            const fileresult = await uploadVideoToS3(file);
            if (!fileresult?.fileName)
              throw new Error(
                `Failed to upload payment condition file: ${file.name}`
              );
            uploadedFileName = fileresult.fileName;
          } else if (file?.PaymentOptions) {
            // Existing file (from DB), reuse its path
            uploadedFileName = file.name;
          }

          if (!uploadedFileName) continue; // Skip if nothing to insert

          const paymentConditionsID = uuidv4();
          const bodyPC = {
            target: "createPaymentConditions",
            paymentConditionsInput: {
              PaymentConditionsID: paymentConditionsID,
              RequirementsID: editRequirementsID,
              PaymentOptions: uploadedFileName,
              TermsConditionsFilePath: "",
              OthersFilePath: "",
              Deleted: 0,
            },
          };
          await postQuery(bodyPC);
          insertedPaymentConditionsIDs.push(paymentConditionsID);
        }

        for (const file of termsAndConditions) {
          let uploadedFileName = null;

          if (file instanceof File) {
            // New file, needs uploading
            const fileresult = await uploadVideoToS3(file);
            if (!fileresult?.fileName)
              throw new Error(
                `Failed to upload payment condition file: ${file.name}`
              );
            uploadedFileName = fileresult.fileName;
          } else if (file?.PaymentOptions) {
            // Existing file (from DB), reuse its path
            uploadedFileName = file.PaymentOptions;
          }

          if (!uploadedFileName) continue; // Skip if nothing to insert
          const paymentConditionsID = uuidv4();
          const bodyTC = {
            target: "createPaymentConditions",
            paymentConditionsInput: {
              PaymentConditionsID: paymentConditionsID,
              RequirementsID: editRequirementsID,
              PaymentOptions: "",
              TermsConditionsFilePath: uploadedFileName,
              OthersFilePath: "",
              Deleted: 0,
            },
          };
          await postQuery(bodyTC);
          insertedPaymentConditionsIDs.push(paymentConditionsID);
        }

        for (const file of otherFiles) {
          let uploadedFileName = null;

          if (file instanceof File) {
            // New file, needs uploading
            const fileresult = await uploadVideoToS3(file);
            if (!fileresult?.fileName)
              throw new Error(
                `Failed to upload payment condition file: ${file.name}`
              );
            uploadedFileName = fileresult.fileName;
          } else if (file?.PaymentOptions) {
            // Existing file (from DB), reuse its path
            uploadedFileName = file.PaymentOptions;
          }

          if (!uploadedFileName) continue; // Skip if nothing to insert
          const paymentConditionsID = uuidv4();
          const bodyOther = {
            target: "createPaymentConditions",
            paymentConditionsInput: {
              PaymentConditionsID: paymentConditionsID,
              RequirementsID: editRequirementsID,
              PaymentOptions: "",
              TermsConditionsFilePath: "",
              OthersFilePath: uploadedFileName,
              Deleted: 0,
            },
          };
          await postQuery(bodyOther);
          insertedPaymentConditionsIDs.push(paymentConditionsID);
        }

        const bodyRTH = {
          target: "createRequirementTxnHistory",
          requirementTxnHistoryInput: {
            RequirementTxnHistoryID: uuidv4(),
            RequirementsID: editRequirementsID,
            ActionedByEmail: activeUser,
            Status: recordStatus,
            Deleted: 0,
          },
        };
        await postQuery(bodyRTH);

        setModal({
          show: true,
          type: "success",
          title: "Success!",
          message: "Requirement updated successfully.",
          autoClose: false,
          nextAction: () => {
            navigate("/requirement");
            handleReset();
            searchRequirements(searchValues);
          },
        });
      } else {
        const body = {
          target: "createRequirements",
          requirementsInput: {
            RequirementsID: uuidv4(),
            RequirementNumber: reqNumber,
            GPEmailID: gpEmail,
            BuyerEmailID: activeUser,
            CreatedByEmailID: activeUser,
            ProductID: product,
            Quantity: requestedQuantity,
            Price: priceOffered,
            DeliveryDate: deliveryPeriodTill,
            RequirementDate: dayjs(curreDate).format("YYYY-MM-DD"),
            MarketPlacement: "No",
            ValidUntil: validUntil,
            Incoterm: incoterm,
            DeliveryLocation: locations,
            DeliveryPeriodFrom: deliveryPeriodFrom,
            DeliveryPeriodTill: deliveryPeriodTill,
            OriginCountry: originCountry,
            ProductPackaging: packageingValue,
            RequestedQuantity: requestedQuantity,
            PriceOffered: priceOffered,
            Status: recordStatus,
            Deleted: 0,
          },
        };

        const result = await postQuery(body);
        if (!result || !result.items)
          throw new Error("Failed to create requirement.");
        insertedRequirementsID = result.items.RequirementsID;

        const bodyProductInfo = {
          target: "createRequirementProductInfo",
          requirementProductInfoInput: {
            RequirementProductInfoID: uuidv4(),
            RequirementsID: insertedRequirementsID,
            Category: category,
            ProductID: product,
            ProductTypeID: productType,
            Deleted: 0,
          },
        };
        await postQuery(bodyProductInfo);

        for (const file of paymentConditionFiles) {
          if (!file || !file.name) continue;
          const fileresult = await uploadVideoToS3(file);
          if (!fileresult?.fileName)
            throw new Error(
              `Failed to upload payment condition file: ${file.name}`
            );
          const paymentConditionsID = uuidv4();
          const bodyPC = {
            target: "createPaymentConditions",
            paymentConditionsInput: {
              PaymentConditionsID: paymentConditionsID,
              RequirementsID: insertedRequirementsID,
              PaymentOptions: fileresult.fileName,
              TermsConditionsFilePath: "",
              OthersFilePath: "",
              Deleted: 0,
            },
          };
          await postQuery(bodyPC);
          insertedPaymentConditionsIDs.push(paymentConditionsID);
        }

        for (const file of termsAndConditions) {
          if (!file || !file.name) continue;
          const fileresult = await uploadVideoToS3(file);
          if (!fileresult?.fileName)
            throw new Error(
              `Failed to upload terms and conditions file: ${file.name}`
            );
          const paymentConditionsID = uuidv4();
          const bodyTC = {
            target: "createPaymentConditions",
            paymentConditionsInput: {
              PaymentConditionsID: paymentConditionsID,
              RequirementsID: insertedRequirementsID,
              PaymentOptions: "",
              TermsConditionsFilePath: fileresult.fileName,
              OthersFilePath: "",
              Deleted: 0,
            },
          };
          await postQuery(bodyTC);
          insertedPaymentConditionsIDs.push(paymentConditionsID);
        }

        for (const file of otherFiles) {
          if (!file || !file.name) continue;
          const fileresult = await uploadVideoToS3(file);
          if (!fileresult?.fileName)
            throw new Error(`Failed to upload other file: ${file.name}`);
          const paymentConditionsID = uuidv4();
          const bodyOther = {
            target: "createPaymentConditions",
            paymentConditionsInput: {
              PaymentConditionsID: paymentConditionsID,
              RequirementsID: insertedRequirementsID,
              PaymentOptions: "",
              TermsConditionsFilePath: "",
              OthersFilePath: fileresult.fileName,
              Deleted: 0,
            },
          };
          await postQuery(bodyOther);
          insertedPaymentConditionsIDs.push(paymentConditionsID);
        }

        const bodyRTH = {
          target: "createRequirementTxnHistory",
          requirementTxnHistoryInput: {
            RequirementTxnHistoryID: uuidv4(),
            RequirementsID: insertedRequirementsID,
            ActionedByEmail: activeUser,
            Status: recordStatus,
            Deleted: 0,
          },
        };
        await postQuery(bodyRTH);

        setModal({
          show: true,
          type: "success",
          title: "Success!",
          message: "Requirement created successfully.",
          autoClose: false,
          nextAction: () => {
            navigate("/requirement");
            handleReset();
            searchRequirements(searchValues);
          },
        });
      }
    } catch (error) {
      console.error("Submission error:", error);
      try {
        await Promise.all(
          insertedPaymentConditionsIDs.map(async (paymentConditionsID) => {
            const bodyDelPC = {
              target: "deletePaymentConditions",
              PaymentConditionsID: paymentConditionsID,
            };
            await postQuery(bodyDelPC);
          })
        );
        if (insertedRequirementsID) {
          const bodyDelR = {
            target: "deleteRequirements",
            RequirementsID: insertedRequirementsID,
          };
          await postQuery(bodyDelR);
        }
      } catch (rollbackError) {
        console.error("Rollback error:", rollbackError);
        setModal({
          show: true,
          type: "error",
          title: "Rollback Error!",
          message:
            "Failed to clean up data after error. Please contact support.",
          autoClose: false,
        });
        setLoading(false);
        setSubmitted(false);
        return;
      }
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: error.message || "Failed to submit requirement.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
      setSubmitted(false);
    }
  };

  const handleOpenTermsModal = useCallback(() => {
    if (!isViewMode) {
      setTempTermsFiles([...termsAndConditions]);
      setOpenTermsModal(true);
    }
  }, [termsAndConditions, isViewMode]);

  const handleCloseTermsModal = useCallback(() => {
    setOpenTermsModal(false);
  }, []);

  const handleAddTempTermsFile = useCallback(() => {
    setTempTermsFiles((prev) => [...prev, null]);
  }, []);

  const handleRemoveTempTermsFile = useCallback(
    (index) => {
      if (tempTermsFiles.length > 0) {
        setTempTermsFiles((prev) => prev.filter((_, i) => i !== index));
      }
    },
    [tempTermsFiles]
  );

  const handleTempTermsFileChange = useCallback(
    (index, event) => {
      const file = event.target.files[0];
      if (file) {
        if (file.type !== "application/pdf") {
          setModal({
            show: true,
            type: "error",
            title: "Invalid File",
            message: "Please upload a PDF file.",
            autoClose: false,
          });
          return;
        }
        if (file.size > 2 * 1024 * 1024) {
          setModal({
            show: true,
            type: "error",
            title: "File Too Large",
            message: "File size must not exceed 2MB.",
            autoClose: false,
          });
          return;
        }
        const updatedFiles = [...tempTermsFiles];
        updatedFiles[index] = file;
        setTempTermsFiles(updatedFiles);
      }
    },
    [tempTermsFiles]
  );

  const handleConfirmTerms = useCallback(() => {
    const validFiles = tempTermsFiles.filter((file) => file !== null);
    // Append only new files, avoiding duplicates by file name
    setTermsAndConditions((prevFiles) => {
      const existingFileNames = new Set(prevFiles.map((file) => file.name));
      const newFiles = validFiles.filter(
        (file) => !existingFileNames.has(file.name)
      );
      return [...prevFiles, ...newFiles];
    });
    setOpenTermsModal(false);
  }, [tempTermsFiles]);

  const handleDeleteTermsFile = useCallback(
    (index) => {
      if (!isViewMode) {
        setTermsAndConditions((prevFiles) =>
          prevFiles.filter((_, i) => i !== index)
        );
      }
    },
    [isViewMode]
  );

  const handleOpenFilesModal = useCallback(() => {
    if (!isViewMode) {
      setTempOtherFiles([...otherFiles]);
      setOpenFilesModal(true);
    }
  }, [otherFiles, isViewMode]);

  const handleCloseFilesModal = useCallback(() => {
    setOpenFilesModal(false);
  }, []);

  const handleAddTempOtherFile = useCallback(() => {
    setTempOtherFiles((prev) => [...prev, null]);
  }, []);

  const handleRemoveTempOtherFile = useCallback(
    (index) => {
      if (tempOtherFiles.length > 0) {
        setTempOtherFiles((prev) => prev.filter((_, i) => i !== index));
      }
    },
    [tempOtherFiles]
  );

  const handleTempOtherFileChange = useCallback(
    (index, event) => {
      const file = event.target.files[0];
      if (file) {
        if (file.type !== "application/pdf") {
          setModal({
            show: true,
            type: "error",
            title: "Invalid File",
            message: "Please upload a PDF file.",
            autoClose: false,
          });
          return;
        }
        if (file.size > 2 * 1024 * 1024) {
          setModal({
            show: true,
            type: "error",
            title: "File Too Large",
            message: "File size must not exceed 2MB.",
            autoClose: false,
          });
          return;
        }
        const updatedFiles = [...tempOtherFiles];
        updatedFiles[index] = file;
        setTempOtherFiles(updatedFiles);
      }
    },
    [tempOtherFiles]
  );

  const handleConfirmOtherFiles = useCallback(() => {
    const validFiles = tempOtherFiles.filter((file) => file !== null);
    // Append only new files, avoiding duplicates by file name
    setOtherFiles((prevFiles) => {
      const existingFileNames = new Set(prevFiles.map((file) => file.name));
      const newFiles = validFiles.filter(
        (file) => !existingFileNames.has(file.name)
      );
      return [...prevFiles, ...newFiles];
    });
    setOpenFilesModal(false);
  }, [tempOtherFiles]);

  const handleDeleteOtherFile = useCallback(
    (index) => {
      if (!isViewMode) {
        setOtherFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
      }
    },
    [isViewMode]
  );

  const handleOpenPaymentConditionModal = useCallback(() => {
    if (!isViewMode) {
      setTempPaymentConditionFiles([...paymentConditionFiles]);
      setOpenPaymentConditionModal(true);
    }
  }, [paymentConditionFiles, isViewMode]);

  const handleClosePaymentConditionModal = useCallback(() => {
    setOpenPaymentConditionModal(false);
  }, []);

  const handleAddTempPaymentConditionFile = useCallback(() => {
    setTempPaymentConditionFiles((prev) => [...prev, null]);
  }, []);

  const handleRemoveTempPaymentConditionFile = useCallback(
    (index) => {
      if (tempPaymentConditionFiles.length > 0) {
        setTempPaymentConditionFiles((prev) =>
          prev.filter((_, i) => i !== index)
        );
      }
    },
    [tempPaymentConditionFiles]
  );

  const handleTempPaymentConditionFileChange = useCallback(
    (index, event) => {
      console.log("event:::", event);
      const file = event.target.files[0];
      if (file) {
        if (file.type !== "application/pdf") {
          setModal({
            show: true,
            type: "error",
            title: "Invalid File",
            message: "Please upload a PDF file.",
            autoClose: false,
          });
          return;
        }
        if (file.size > 2 * 1024 * 1024) {
          setModal({
            show: true,
            type: "error",
            title: "File Too Large",
            message: "File size must not exceed 2MB.",
            autoClose: false,
          });
          return;
        }
        const updatedFiles = [...tempPaymentConditionFiles];
        updatedFiles[index] = file;
        setTempPaymentConditionFiles(updatedFiles);
      }
    },
    [tempPaymentConditionFiles]
  );

  const handleConfirmPaymentCondition = useCallback(() => {
    const validFiles = tempPaymentConditionFiles.filter(
      (file) => file !== null
    );
    //Append only new files, avoiding duplicates by file name
    setPaymentConditionFiles((prevFiles) => {
      const existingFileNames = new Set(prevFiles.map((file) => file.name));
      const newFiles = validFiles.filter(
        (file) => !existingFileNames.has(file.name)
      );

      return [...prevFiles, ...newFiles];
    });

    setOpenPaymentConditionModal(false);
  }, [tempPaymentConditionFiles]);

  const handleDeletePaymentConditionFile = useCallback(
    (index) => {
      if (!isViewMode) {
        setPaymentConditionFiles((prevFiles) =>
          prevFiles.filter((_, i) => i !== index)
        );
      }
    },
    [isViewMode]
  );

  useEffect(() => {
    if (activeUser) searchRequirements(searchValues);
  }, [rowStart, pageSize, activeUser]);

  const handlePageChange = (page) => {
    const newRowStart = (page - 1) * pageSize;
    setRowStart(newRowStart);
  };

  const currentPage = Math.floor(rowStart / pageSize) + 1;

  return (
    <div className="amtpl-dashboard">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <Header activeUser={activeUser} setLoading={setLoading} />
      <div className="content">
        <SidebarBuyer
          selectedFilter={selectedFilter}
          handleDataFilterChange={handleDataFilterChange}
        />

        <div className="main-content">
          <div className="table-header">
            <div className="header-text">
              <div className="head-text">Requirements</div>
              <div className="head-subtext">
                All Requirements data will be displayed here.
              </div>
            </div>
            <div className="head-btn">
              <div className="export"></div>
            </div>
          </div>
          <div className="search-filter row">
            <SearchFilter fields={fields} onSearch={handleSearch} />
          </div>
          <DataTable
            columns={columns}
            data={requirementList}
            total={totalItems}
            title="Requirement Information"
            onRowClick={handleRowClick}
            rowsPerPage={pageSize}
            onPageChange={handlePageChange}
            currentPage={currentPage}
          />
          <Box
            sx={{
              width: "100%",
              marginTop: 2,
              boxShadow: "1px 1px 5px #ccc",
              padding: "15px",
            }}
          >
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "#de6b14",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingBottom: 1,
              }}
            >
              <Typography variant="h6">
                {isFormActive
                  ? isEditMode
                    ? "Edit Requirement"
                    : "Add Requirement"
                  : isViewMode
                  ? "View Requirement"
                  : "Requirement Form"}
              </Typography>
              <Box>
                {(isFormActive || isViewMode) && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleReset}
                    sx={{ marginRight: 1 }}
                  >
                    Reset
                  </Button>
                )}
                {!isFormActive && !isViewMode && (
                  <Button
                    variant="contained"
                    onClick={handleAddClick}
                    sx={{ marginRight: 1, backgroundColor: "#ff9800" }}
                  >
                    Add
                  </Button>
                )}
                {isViewMode && selectedRow?.Status === "Rejected" && (
                  <Button
                    variant="contained"
                    onClick={handleEditClick}
                    sx={{ marginRight: 1, backgroundColor: "#1976d2" }}
                  >
                    Edit
                  </Button>
                )}
                {isFormActive && (
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#ff9800",
                      "&:hover": { backgroundColor: "#de6b14" },
                    }}
                    onClick={handleSubmit}
                    disabled={submitted}
                  >
                    {submitted
                      ? "Submitting..."
                      : isEditMode
                      ? "Update"
                      : "Submit"}
                  </Button>
                )}
              </Box>
            </Box>
            {(isFormActive || isViewMode) && (
              <>
                <Box sx={{ borderBottom: 1, borderColor: "#de6b14" }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="Basic info tabs"
                    sx={{
                      "& .MuiTab-root": {
                        color: "#000",
                        backgroundColor: "transparent",
                        "&.Mui-selected": {
                          color: "#fff",
                          backgroundColor: "#de6b14",
                          borderBottom: "2px solid #d4d4d4",
                        },
                      },
                      "& .MuiTabs-indicator": { backgroundColor: "#de6b14" },
                    }}
                  >
                    <Tab
                      label="Basic Info"
                      id="tab-0"
                      aria-controls="tabpanel-0"
                    />
                    <Tab
                      label="General"
                      id="tab-1"
                      aria-controls="tabpanel-1"
                    />
                    <Tab
                      label="Product Details"
                      id="tab-2"
                      aria-controls="tabpanel-2"
                    />
                    <Tab
                      label="Quantity & Packaging"
                      id="tab-3"
                      aria-controls="tabpanel-3"
                    />
                  </Tabs>
                </Box>
                <TabPanel value={tabValue} index={0}>
                  <BasicInfoTab
                    category={category}
                    setCategory={setCategory}
                    product={product}
                    setProduct={setProduct}
                    productType={productType}
                    setProductType={setProductType}
                    productMaster={productMaster}
                    productCategoryMaster={productCategoryMaster}
                    loadProducts={loadProducts}
                    loadProductTypes={loadProductTypes}
                    productTypeMaster={productTypeMaster}
                    errors={errors}
                    isViewMode={isViewMode}
                  />
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                  <GeneralTab
                    validUntil={validUntil}
                    setValidUntil={setValidUntil}
                    incoterm={incoterm}
                    setIncoterm={setIncoterm}
                    locations={locations}
                    setLocations={setLocations}
                    deliveryPeriodFrom={deliveryPeriodFrom}
                    setDeliveryPeriodFrom={setDeliveryPeriodFrom}
                    deliveryPeriodTill={deliveryPeriodTill}
                    setDeliveryPeriodTill={setDeliveryPeriodTill}
                    paymentConditionFiles={paymentConditionFiles}
                    handleOpenPaymentConditionModal={
                      handleOpenPaymentConditionModal
                    }
                    handleDeletePaymentConditionFile={
                      handleDeletePaymentConditionFile
                    }
                    dataIncoterms={dataIncoterms}
                    errors={errors}
                    isViewMode={isViewMode}
                  />
                </TabPanel>
                <TabPanel value={tabValue} index={2}>
                  <ProductDetailsTab
                    originCountry={originCountry}
                    setOriginCountry={setOriginCountry}
                    termsAndConditions={termsAndConditions}
                    handleOpenTermsModal={handleOpenTermsModal}
                    handleDeleteTermsFile={handleDeleteTermsFile}
                    otherFiles={otherFiles}
                    handleOpenFilesModal={handleOpenFilesModal}
                    handleDeleteOtherFile={handleDeleteOtherFile}
                    countrymaster={countrymaster}
                    errors={errors}
                    isViewMode={isViewMode}
                  />
                </TabPanel>
                <TabPanel value={tabValue} index={3}>
                  <QuantityPackagingTab
                    productPackaging={productPackaging}
                    setProductPackaging={setProductPackaging}
                    packageSize={packageSize}
                    setPackageSize={setPackageSize}
                    requestedQuantity={requestedQuantity}
                    setRequestedQuantity={setRequestedQuantity}
                    priceOffered={priceOffered}
                    setPriceOffered={setPriceOffered}
                    errors={errors}
                    isViewMode={isViewMode}
                  />
                </TabPanel>
              </>
            )}
          </Box>
          <Modal open={openTermsModal} onClose={handleCloseTermsModal}>
            <Paper
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 550,
                p: 4,
                bgcolor: "background.paper",
                boxShadow: 24,
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography variant="h6">Upload Terms & Condition</Typography>
                <IconButton onClick={handleCloseTermsModal}>
                  <CloseIcon />
                </IconButton>
              </Box>
              {tempTermsFiles.map((file, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  <TextField
                    type="file"
                    onChange={(e) => handleTempTermsFileChange(index, e)}
                    sx={{ flexGrow: 1, mr: 1 }}
                    inputProps={{
                      accept: "application/pdf",
                      "aria-label": `Terms File ${index + 1}`,
                    }}
                  />
                  {file && (
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {file.name}
                    </Typography>
                  )}
                  <IconButton
                    onClick={() => handleRemoveTempTermsFile(index)}
                    disabled={tempTermsFiles.length === 0}
                  >
                    <RemoveIcon
                      sx={{
                        backgroundColor: "#fb8c00",
                        color: "#fff",
                        borderRadius: "24px",
                        width: "35px",
                        height: "35px",
                      }}
                    />
                  </IconButton>
                  <IconButton onClick={handleAddTempTermsFile}>
                    <AddIcon
                      sx={{
                        backgroundColor: "#fb8c00",
                        color: "#fff",
                        borderRadius: "24px",
                        width: "35px",
                        height: "35px",
                        "&:hover": { backgroundColor: "#fb8c00" },
                      }}
                    />
                  </IconButton>
                </Box>
              ))}
              {tempTermsFiles.length === 0 && (
                <Button
                  variant="outlined"
                  onClick={handleAddTempTermsFile}
                  sx={{ mb: 2 }}
                >
                  Add File
                </Button>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={handleConfirmTerms}
                  sx={{
                    backgroundColor: "#ff9800",
                    "&:hover": { backgroundColor: "#fb8c00" },
                  }}
                >
                  Confirm
                </Button>
              </Box>
            </Paper>
          </Modal>
          <Modal open={openFilesModal} onClose={handleCloseFilesModal}>
            <Paper
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 550,
                p: 4,
                bgcolor: "background.paper",
                boxShadow: 24,
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography variant="h6">Upload Other Files</Typography>
                <IconButton onClick={handleCloseFilesModal}>
                  <CloseIcon />
                </IconButton>
              </Box>
              {tempOtherFiles.map((file, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  <TextField
                    type="file"
                    onChange={(e) => handleTempOtherFileChange(index, e)}
                    sx={{ flexGrow: 1, mr: 1 }}
                    inputProps={{
                      accept: "application/pdf",
                      "aria-label": `Payment Condition File ${index + 1}`,
                    }}
                  />
                  {file && (
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {file.name}
                    </Typography>
                  )}
                  <IconButton
                    onClick={() => handleRemoveTempOtherFile(index)}
                    disabled={tempOtherFiles.length === 0}
                  >
                    <RemoveIcon
                      sx={{
                        backgroundColor: "#fb8c00",
                        color: "#fff",
                        borderRadius: "24px",
                        width: "35px",
                        height: "35px",
                      }}
                    />
                  </IconButton>
                  <IconButton onClick={handleAddTempOtherFile}>
                    <AddIcon
                      sx={{
                        backgroundColor: "#fb8c00",
                        color: "#fff",
                        borderRadius: "24px",
                        width: "35px",
                        height: "35px",
                        "&:hover": { backgroundColor: "#fb8c00" },
                      }}
                    />
                  </IconButton>
                </Box>
              ))}
              {tempOtherFiles.length === 0 && (
                <Button
                  variant="outlined"
                  onClick={handleAddTempOtherFile}
                  sx={{ mb: 2 }}
                >
                  Add File
                </Button>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={handleConfirmOtherFiles}
                  sx={{
                    backgroundColor: "#ff9800",
                    "&:hover": { backgroundColor: "#fb8c00" },
                  }}
                >
                  Confirm
                </Button>
              </Box>
            </Paper>
          </Modal>
          <Modal
            open={openPaymentConditionModal}
            onClose={handleClosePaymentConditionModal}
          >
            <Paper
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 550,
                p: 4,
                bgcolor: "background.paper",
                boxShadow: 24,
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography variant="h6">Upload Payment Condition</Typography>
                <IconButton onClick={handleClosePaymentConditionModal}>
                  <CloseIcon />
                </IconButton>
              </Box>
              {tempPaymentConditionFiles.map((file, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", alignItems: "center", mb: 2 }}
                >
                  <TextField
                    type="file"
                    onChange={(e) =>
                      handleTempPaymentConditionFileChange(index, e)
                    }
                    sx={{ flexGrow: 1, mr: 1 }}
                    inputProps={{
                      accept: "application/pdf",
                      "aria-label": `Payment Condition File ${index + 1}`,
                    }}
                  />
                  {file && (
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {file.name}
                    </Typography>
                  )}
                  <IconButton
                    onClick={() => handleRemoveTempPaymentConditionFile(index)}
                    disabled={tempPaymentConditionFiles.length === 0}
                  >
                    <RemoveIcon
                      sx={{
                        backgroundColor: "#fb8c00",
                        color: "#fff",
                        borderRadius: "24px",
                        width: "35px",
                        height: "35px",
                      }}
                    />
                  </IconButton>
                  <IconButton onClick={handleAddTempPaymentConditionFile}>
                    <AddIcon
                      sx={{
                        backgroundColor: "#fb8c00",
                        color: "#fff",
                        borderRadius: "24px",
                        width: "35px",
                        height: "35px",
                        "&:hover": { backgroundColor: "#fb8c00" },
                      }}
                    />
                  </IconButton>
                </Box>
              ))}
              {tempPaymentConditionFiles.length === 0 && (
                <Button
                  variant="outlined"
                  onClick={handleAddTempPaymentConditionFile}
                  sx={{ mb: 2 }}
                >
                  Add File
                </Button>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={handleConfirmPaymentCondition}
                  sx={{
                    backgroundColor: "#ff9800",
                    "&:hover": { backgroundColor: "#fb8c00" },
                  }}
                >
                  Confirm
                </Button>
              </Box>
            </Paper>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default RequirementModule;
